import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import * as AWS from 'aws-sdk/global';
import * as awsservice from 'aws-sdk/lib/service';
import * as CognitoIdentity from 'aws-sdk/clients/cognitoidentity';
import * as CognitoIdentityServiceProvider from 'aws-sdk/clients/cognitoidentityserviceprovider';

export interface CognitoCallback {
  cognitoCallback(message: string, result: any, session: any, error?: any): void;

  handleMFAStep?(
    challengeName: string,
    challengeParameters: ChallengeParameters,
    callback: (confirmationCode: string) => any
  ): void;
}

export interface LoggedInCallback {
  isLoggedIn(message: string, loggedIn: boolean): void;
}

export interface ChallengeParameters {
  CODE_DELIVERY_DELIVERY_MEDIUM: string;

  CODE_DELIVERY_DESTINATION: string;
}

export interface Callback {
  callback(): void;

  callbackWithParam(result: any): void;
}

@Injectable()
export class CognitoUtil {
  public static _REGION = environment.region;

  public static _IDENTITY_POOL_ID = environment.identityPoolId;
  public static _USER_POOL_ID = environment.userPoolId;
  public static _CLIENT_ID = environment.clientId;
  public static _DOMAIN = environment.domain;

  public static _POOL_DATA: any = {
    UserPoolId: CognitoUtil._USER_POOL_ID,
    ClientId: CognitoUtil._CLIENT_ID
  };

  private cognitoIdentityServiceProvider;

  public cognitoCreds: AWS.CognitoIdentityCredentials;

  constructor() {
    this.cognitoIdentityServiceProvider = new CognitoIdentityServiceProvider({ region: CognitoUtil._REGION });
  }

  getUserPool() {
    if (environment.cognito_idp_endpoint) {
      CognitoUtil._POOL_DATA.endpoint = environment.cognito_idp_endpoint;
    }

    return new CognitoUserPool(CognitoUtil._POOL_DATA);
  }

  getCurrentUser() {
    return this.getUserPool().getCurrentUser();
  }

  getCurrentUserSession() {
    // this.getUserPool().getCurrentUser().getCognitoUserSession();
    // return this.cognitoUser;
  }

  // AWS Stores Credentials in many ways, and with TypeScript this means that
  // getting the base credentials we authenticated with from the AWS globals gets really murky,
  // having to get around both class extension and unions. Therefore, we're going to give
  // developers direct access to the raw, unadulterated CognitoIdentityCredentials
  // object at all times.
  setCognitoCreds(creds: AWS.CognitoIdentityCredentials) {
    this.cognitoCreds = creds;
  }

  getCognitoCreds() {
    return this.cognitoCreds;
  }

  // This method takes in a raw jwtToken and uses the global AWS config options to build a
  // CognitoIdentityCredentials object and store it for us. It also returns the object to the caller
  // to avoid unnecessary calls to setCognitoCreds.

  buildCognitoCreds(idTokenJwt: string) {
    let url = 'cognito-idp.' + CognitoUtil._REGION.toLowerCase() + '.amazonaws.com/' + CognitoUtil._USER_POOL_ID;
    if (environment.cognito_idp_endpoint) {
      url = environment.cognito_idp_endpoint + '/' + CognitoUtil._USER_POOL_ID;
    }
    const logins: CognitoIdentity.LoginsMap = {};
    logins[url] = idTokenJwt;
    const params = {
      IdentityPoolId: CognitoUtil._IDENTITY_POOL_ID /* required */,
      Logins: logins
    };
    const serviceConfigs = <awsservice.ServiceConfigurationOptions>{};
    if (environment.cognito_identity_endpoint) {
      serviceConfigs.endpoint = environment.cognito_identity_endpoint;
    }

    serviceConfigs.region = CognitoUtil._REGION;
    const creds = new AWS.CognitoIdentityCredentials(params, serviceConfigs);
    this.setCognitoCreds(creds);
    this.cognitoIdentityServiceProvider.config.credentials = creds;
    return creds;
  }

  refreshCredentials(credentials: AWS.CognitoIdentityCredentials): Promise<any> {
    return new Promise((resolve, reject) => {
      credentials.refresh(function(error) {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    });
  }

  getCognitoIdentity(): string {
    return this.cognitoCreds.identityId;
  }

  getAccessToken(callback: Callback): void {
    if (callback == null) {
      throw new Error('CognitoUtil: callback in getAccessToken is null...returning');
    }
    if (this.getCurrentUser() != null) {
      this.getCurrentUser().getSession(function(err, session) {
        if (err) {
          console.log("CognitoUtil: Can't set the credentials:" + err);
          callback.callbackWithParam(null);
        } else {
          if (session.isValid()) {
            callback.callbackWithParam(session.getAccessToken().getJwtToken());
          }
        }
      });
    } else {
      callback.callbackWithParam(null);
    }
  }

  getJwtToken(): string {
    let ret = null;
    if (this.getCurrentUser() != null) {
      this.getCurrentUser().getSession((err, session) => {
        if (!err && session.isValid()) {
          ret = session.getAccessToken().getJwtToken();
        }
      });
    }
    return ret;
  }

  getRefreshToken(callback: Callback): void {
    if (callback == null) {
      throw new Error('CognitoUtil: callback in getRefreshToken is null...returning');
    }
    if (this.getCurrentUser() != null) {
      this.getCurrentUser().getSession(function(err, session) {
        if (err) {
          console.log("CognitoUtil: Can't set the credentials:" + err);
          callback.callbackWithParam(null);
        } else {
          if (session.isValid()) {
            callback.callbackWithParam(session.getRefreshToken());
          }
        }
      });
    } else {
      callback.callbackWithParam(null);
    }
  }

  refresh(): Promise<boolean> {
    return new Promise(resolve => {
      this.getCurrentUser().getSession(function(err, session) {
        if (err) {
          console.log("CognitoUtil: Can't set the credentials:" + err);
          resolve(false);
        } else {
          if (session.isValid()) {
            console.log('CognitoUtil: refreshed successfully');
            resolve(true);
          } else {
            console.log('CognitoUtil: refreshed but session is still not valid');
            resolve(false);
          }
        }
      });
    });
  }

  getGroups(): Promise<any> {
    return new Promise(resolve => {
      this.getCurrentUser().getSession(function(err, session) {
        if (session) {
          const groups = session.getIdToken().payload['cognito:groups'] || [];
          resolve(groups);
        }
        resolve(null);
      });
    });
  }

  getAuthData(): Promise<any> {
    return new Promise(resolve => {
      const currentUser = this.getCurrentUser();

      if (currentUser === null) {
        resolve(null);
        return;
      }

      currentUser.getSession(function(err, session) {
        if (session) {
          const groups = session.getIdToken().payload['cognito:groups'] || [];
          const apps = session.getIdToken().payload['custom:apps'] || 0;
          const result = {
            groups: groups,
            apps: apps
          };
          resolve(result);
        }
        resolve(null);
      });
    });
  }

  // async refreshSession(): Promise<any> {

  //   const loggedIn = await this.refresh();

  //   return new Promise((resolve, reject) => {
  //     if (!loggedIn) {
  //       reject();
  //       return null;
  //     }

  //     this.getCurrentUser().getSession((err, session) => {
  //       if (err || !session) {
  //         reject();
  //         return null;
  //       }
  //       if (session) {
  //         const credentials = this.buildCognitoCreds(session.getIdToken().getJwtToken());
  //         this.refreshCredentials(credentials).then(() => resolve());
  //       }
  //     });
  //   });
  // }

  // --------------------------------------------------------------------------------
  // ADMIN FUNCTIONALITIES - START

  async getUser(userId): Promise<any> {
    // await this.refreshSession();

    const params = {
      Username: userId,
      UserPoolId: CognitoUtil._USER_POOL_ID
    };

    return new Promise((resolve, reject) => {
      this.cognitoIdentityServiceProvider.adminGetUser(params, (err, response) => {
        if (err) {
          // an error occurred
          reject(err);
          return null;
        } else {
          // successful response
          const userAttributesToShow = ['email', 'custom:name', 'custom:roles', 'custom:apps'];
          const usersTableData = {
            username: response.Username,
            status: response.UserStatus,
            enabled: response.Enabled
          };

          for (
            let userAttributesIndex = 0;
            userAttributesIndex < response.UserAttributes.length;
            userAttributesIndex++
          ) {
            const attribute = response.UserAttributes[userAttributesIndex];
            if (userAttributesToShow.indexOf(attribute.Name) >= 0) {
              if (attribute.Name.indexOf(':') >= 0) {
                attribute.Name = attribute.Name.replace(':', '_');
              }
              usersTableData[attribute.Name] = attribute.Value;
            }
          }
          resolve(usersTableData);
        }
      });
    });
  }

  async updateUser(user): Promise<any> {
    // await this.refreshSession();
    const userAttributes = [];

    if (typeof user.custom_roles !== 'undefined' && user.custom_roles !== null) {
      userAttributes.push({
        Name: 'custom:roles',
        Value: user.custom_roles
      });
    }

    if (typeof user.custom_apps !== 'undefined' && user.custom_apps !== null) {
      userAttributes.push({
        Name: 'custom:apps',
        Value: user.custom_apps
      });
    }

    if (typeof user.custom_name !== 'undefined' && user.custom_name !== null) {
      userAttributes.push({
        Name: 'custom:name',
        Value: user.custom_name
      });
    }

    const params = {
      Username: user.username,
      UserPoolId: CognitoUtil._USER_POOL_ID,
      UserAttributes: userAttributes
    };

    return new Promise((resolve, reject) => {
      this.cognitoIdentityServiceProvider.adminUpdateUserAttributes(params, (err, response) => {
        if (err) {
          // an error occurred
          reject(err);
        } else {
          // successful response
          resolve();
        }
      });
    });
  }

  async listUsers(): Promise<any> {
    // await this.refreshSession();

    const promises = [];
    promises.push(this.listAllUsers());
    promises.push(this.listUsersInGroup('users'));
    promises.push(this.listUsersInGroup('administrators'));

    return new Promise((resolve, reject) => {
      Promise.all(promises).then(
        result => {
          const allUsers = result[0];
          const users = result[1];
          const administrators = result[2];

          const usersMap = users.map(function(e) {
            return e.username;
          });
          const administratorsMap = administrators.map(function(e) {
            return e.username;
          });

          for (let allUsersIndex = 0; allUsersIndex < allUsers.length; allUsersIndex++) {
            // If the user is both in users and administrators
            if (
              usersMap.indexOf(allUsers[allUsersIndex].username) >= 0 &&
              administratorsMap.indexOf(allUsers[allUsersIndex].username) >= 0
            ) {
              allUsers[allUsersIndex].groups = ['users', 'administrators'];
            } else if (usersMap.indexOf(allUsers[allUsersIndex].username) >= 0) {
              allUsers[allUsersIndex].groups = ['users'];
            } else if (administratorsMap.indexOf(allUsers[allUsersIndex].username) >= 0) {
              allUsers[allUsersIndex].groups = ['administrators'];
            }
          }
          resolve(
            allUsers.sort((a, b) => {
              if (a.email.toLowerCase() < b.email.toLowerCase()) {
                return -1;
              } else if (a.email.toLowerCase() > b.email.toLowerCase()) {
                return 1;
              } else {
                return 0;
              }
            })
          );
        },
        err => {
          reject(err);
        }
      );
    });
  }

  private listUsersInGroup(groupName): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = {
        GroupName: groupName,
        UserPoolId: CognitoUtil._USER_POOL_ID
      };

      this.cognitoIdentityServiceProvider.listUsersInGroup(params, (err, response) => {
        if (err) {
          console.log(err, err.stack); // an error occurred
          reject(err);
        } else {
          // successful response
          const userAttributesToShow = ['email', 'custom:name', 'custom:roles', 'custom:apps'];
          const usersTableData = response.Users.map(function(user) {
            const tmpObj = {
              username: user.Username,
              status: user.UserStatus,
              enabled: user.Enabled,
              groups: [groupName],
              mappedRoles: null
            };

            for (let userAttributesIndex = 0; userAttributesIndex < user.Attributes.length; userAttributesIndex++) {
              const attribute = user.Attributes[userAttributesIndex];
              if (userAttributesToShow.indexOf(attribute.Name) >= 0) {
                if (attribute.Name.indexOf(':') >= 0) {
                  attribute.Name = attribute.Name.replace(':', '_');
                }
                tmpObj[attribute.Name] = attribute.Value;
                // Roles mapping
                if (attribute.Name === 'custom_roles') {
                  if (attribute.Value === '0001') {
                    tmpObj.mappedRoles = 'Leader';
                  } else {
                    tmpObj.mappedRoles = '';
                  }
                }
              }
            }
            return tmpObj;
          });
          resolve(usersTableData);
        }
      });
    });
  }

  private populateUsersData(users) {
    const userAttributesToShow = ['email', 'custom:name', 'custom:roles', 'custom:apps'];
    return users.map(function(user) {
      const tmpObj = {
        username: user.Username,
        status: user.UserStatus,
        enabled: user.Enabled,
        groups: [],
        mappedRoles: null
      };

      for (let userAttributesIndex = 0; userAttributesIndex < user.Attributes.length; userAttributesIndex++) {
        const attribute = user.Attributes[userAttributesIndex];
        if (userAttributesToShow.indexOf(attribute.Name) >= 0) {
          if (attribute.Name.indexOf(':') >= 0) {
            attribute.Name = attribute.Name.replace(':', '_');
          }
          tmpObj[attribute.Name] = attribute.Value;
          // Roles mapping
          if (attribute.Name === 'custom_roles') {
            if (attribute.Value === '0001') {
              tmpObj.mappedRoles = 'Leader';
            } else {
              tmpObj.mappedRoles = '';
            }
          }
        }
      }
      return tmpObj;
    });
  }

  private listAllUsers(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const users = [];
      let PaginationToken = null;

      try {
        while (PaginationToken !== undefined) {
          const params: any = {
            UserPoolId: CognitoUtil._USER_POOL_ID
          };
          if (PaginationToken) {
            params.PaginationToken = PaginationToken;
          }
          const response = await this.requestUserPage(params);
          users.push(...response.Users);
          PaginationToken = response.PaginationToken;
        }
      } catch (e) {
        reject(e);
      }

      resolve(this.populateUsersData(users));
    });
  }

  private requestUserPage(params) {
    return new Promise<any>((resolve, reject) => {
      this.cognitoIdentityServiceProvider.listUsers(params, (err, response) => {
        if (err) {
          reject(err);
        } else {
          resolve(response);
        }
      });
    });
  }

  resetPassword(username) {
    return new Promise((resolve, reject) => {
      const params = {
        UserPoolId: CognitoUtil._USER_POOL_ID,
        Username: username
      };
      this.cognitoIdentityServiceProvider.adminResetUserPassword(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  recreateUser(username) {
    return new Promise((resolve, reject) => {
      const params = {
        UserPoolId: CognitoUtil._USER_POOL_ID,
        Username: username,
        MessageAction: 'RESEND'
      };
      this.cognitoIdentityServiceProvider.adminCreateUser(params, (err, data) => {
        if (err) {
          reject(err);
        } else {
          resolve(data);
        }
      });
    });
  }

  // ADMIN FUNCTIONALITIES - END
  // --------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------
  // PLANNER FUNCTIONALITIES - START

  async getCurrentUserAttribute(attributeName): Promise<any> {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser();

      if (cognitoUser === null) {
        reject(new Error('Not possible to find a valid current user.'));
        return null;
      }

      cognitoUser.getSession(function(err, session) {
        if (err) {
          reject(err);
        } else {
          if (!session.isValid()) {
            reject(new Error('Not possible to find a valid session.'));
            return null;
          }

          cognitoUser.getUserAttributes((error, result) => {
            if (error) {
              console.log('getCurrentUserAttribute - Reject - ' + error);
              reject(error);
            } else {
              let attrValue = null;
              for (let attributesIndex = 0; attributesIndex < result.length; attributesIndex++) {
                if (result[attributesIndex].getName() === attributeName) {
                  attrValue = result[attributesIndex].getValue();
                  break;
                }
              }
              if (typeof attrValue === 'undefined' || attrValue === null || attrValue.length <= 0) {
                reject(
                  new Error('Not possible to find a valid [' + attributeName + '] attribute for the current user')
                );
              } else {
                resolve(attrValue);
              }
            }
          });
        }
      });
    });
  }

  // PLANNER FUNCTIONALITIES - END
  // --------------------------------------------------------------------------------
}



// WEBPACK FOOTER //
// ./src/app/shared/services/cognito.service.ts